<template>
  <div class="pc-index">
    <!-- 首屏 Loading：banner-wrap 内全部 img load/error 后关闭，超时见 PAGE_LOADING_FALLBACK_MS -->
    <div
      v-show="pageLoadingVisible"
      class="pc-index__page-loading"
      aria-live="polite"
      :aria-busy="pageLoadingVisible ? 'true' : 'false'"
      aria-label="loading"
    >
      <div class="pc-index__page-loading__inner">
        <img
          class="pc-index__page-loading__title"
          src="@/assets/image/index/banner_title.webp"
          width="660"
          height="240"
          alt="loading"
          decoding="async"
          fetchpriority="high"
        />
      </div>
    </div>

    <!-- banner -->
    <header
      ref="bannerWrap"
      class="pc-index__banner-wrap"
      aria-label="Event hero and key visual"
    >
      <img
        class="pc-index__banner"
        src="@/assets/image/index/banner.webp"
        width="1920"
        height="1080"
        alt="Blue Archive 5th Anniversary DJ — key visual"
        decoding="async"
        fetchpriority="high"
      />
      <img
        class="pc-index__overlay pc-index__overlay--title"
        src="@/assets/image/index/banner_title.webp"
        width="660"
        height="240"
        alt="Blue Archive 5th Anniversary DJ — title artwork"
        decoding="async"
      />
      <img
        class="pc-index__overlay pc-index__overlay--role"
        src="@/assets/image/index/role_name.webp"
        width="1360"
        height="420"
        alt="Character and event name artwork"
        decoding="async"
      />
      <!-- 无限滚动横幅 -->
      <div class="pc-index__banner-wrap-footer">
        <div class="pc-index__banner-wrap-footer-inner-notify">
          <div class="pc-index__banner-wrap-footer-inner-notify-track">
            <div
              v-for="dup in 2"
              :key="'marquee-' + dup"
              class="pc-index__banner-wrap-footer-inner-notify-group"
              :aria-hidden="dup === 2 ? 'true' : undefined"
            >
              <img
                v-for="item in 10"
                :key="`${dup}-${item}`"
                class="pc-index__banner-wrap-footer-inner-notify-title"
                src="@/assets/image/index/banner_footer_title.webp"
                width="314"
                height="30"
                alt="Scrolling footer title strip (decorative)"
                aria-hidden="true"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- NEWS 区渐变 + 四角装饰：视口固定全局层（不随文档滚动） -->
    <div class="pc-index__news-global-bg" aria-hidden="true">
      <img
        class="pc-index__news-global-bg__star-left"
        src="@/assets/image/index/icon_star.webp"
        width="180"
        height="180"
        alt=""
        decoding="async"
        loading="lazy"
      />
      <img
        class="pc-index__news-global-bg__star-right"
        src="@/assets/image/index/icon_star.webp"
        width="100"
        height="100"
        alt=""
        decoding="async"
        loading="lazy"
      />
      <img
        class="pc-index__news-global-bg__circular-left"
        src="@/assets/image/index/icon_circular.webp"
        width="200"
        height="200"
        alt=""
        decoding="async"
        loading="lazy"
      />
      <img
        class="pc-index__news-global-bg__circular-right"
        src="@/assets/image/index/icon_circular.webp"
        width="260"
        height="260"
        alt=""
        decoding="async"
        loading="lazy"
      />
    </div>

    <!-- DJ 无限滚动底纹：视口固定，叠在 __news-global-bg 上；ABOUT 进入视口起挂载预跑动画（opacity 0），DJ 进入视口后渐显 -->
    <div
      v-if="djGlobalBgLayerActive"
      class="pc-index__dj-global-bg"
      :class="{ 'pc-index__dj-global-bg--visible': djGlobalBgLayerVisible }"
      aria-hidden="true"
    >
      <div class="pc-index__dj-global-bg__layer">
        <div class="pc-index__dj-global-bg__track">
          <div
            v-for="dup in 9"
            :key="'dj-global-bg-slab-' + dup"
            class="pc-index__dj-global-bg__slab"
          />
        </div>
      </div>
    </div>

    <!-- NEWS -->
    <section class="pc-index__news-wrap" aria-label="NEWS">
      <img
        class="pc-index__news-wrap-title"
        src="@/assets/image/index/news_title.webp"
        width="697"
        height="68"
        alt="NEWS section title graphic"
        decoding="async"
        loading="lazy"
      />

      <div ref="newsScrollRoot" class="pc-index__news-wrap-list">
        <div ref="newsItemsMeasureRoot" class="pc-index__news-wrap-list-items">
          <div
            ref="newsRealItemsMeasureRoot"
            class="pc-index__news-wrap-list-real-items"
          >
            <div v-for="item in newsList" :key="item.id">
              <div class="pc-index__news-wrap-list-item">
                <img
                  v-if="item.isNew"
                  class="pc-index__news-wrap-list-item-icon"
                  src="@/assets/image/index/icon_new.webp"
                  width="59"
                  height="59"
                  alt="New announcement badge"
                  decoding="async"
                  loading="lazy"
                />
                <div class="pc-index__news-wrap-list-item-date">
                  {{ item.date }}
                </div>
                <div class="pc-index__news-wrap-list-item-content">
                  <div
                    class="pc-index__news-wrap-list-item-content-text"
                    v-html="item.content"
                  ></div>
                </div>
              </div>
            </div>
          </div>
          <div
            v-for="n in visibleNewsPlaceholderCount"
            :key="'news-coming-soon-' + n"
            class="pc-index__news-wrap-list-item pc-index__news-wrap-list-item--coming-soon"
            role="status"
          >
            <span class="pc-index__news-wrap-list-item-coming-en">
              {{ pcIndexCopy.news.comingSoonEn }}
            </span>
            <span class="pc-index__news-wrap-list-item-coming-ja" lang="ja">
              {{ pcIndexCopy.news.comingSoonJa }}
            </span>
          </div>
        </div>
      </div>
    </section>

    <!-- ABOUT：装饰小图标（1–6 左 / 7–11 右，细调见 .pc-index__about-bg-icon--n） -->
    <section ref="aboutWrap" class="pc-index__about-wrap" aria-label="ABOUT">
      <img
        class="pc-index__about-bg-icon pc-index__about-bg-icon--1"
        src="@/assets/image/index/about_bg_icon1.webp"
        alt=""
        aria-hidden="true"
        decoding="async"
        loading="lazy"
      />
      <img
        class="pc-index__about-bg-icon pc-index__about-bg-icon--2"
        src="@/assets/image/index/about_bg_icon2.webp"
        alt=""
        aria-hidden="true"
        decoding="async"
        loading="lazy"
      />
      <img
        class="pc-index__about-bg-icon pc-index__about-bg-icon--3"
        src="@/assets/image/index/about_bg_icon3.webp"
        alt=""
        aria-hidden="true"
        decoding="async"
        loading="lazy"
      />
      <img
        class="pc-index__about-bg-icon pc-index__about-bg-icon--4"
        src="@/assets/image/index/about_bg_icon4.webp"
        alt=""
        aria-hidden="true"
        decoding="async"
        loading="lazy"
      />
      <img
        class="pc-index__about-bg-icon pc-index__about-bg-icon--5"
        src="@/assets/image/index/about_bg_icon5.webp"
        alt=""
        aria-hidden="true"
        decoding="async"
        loading="lazy"
      />
      <img
        class="pc-index__about-bg-icon pc-index__about-bg-icon--6"
        src="@/assets/image/index/about_bg_icon6.webp"
        alt=""
        aria-hidden="true"
        decoding="async"
        loading="lazy"
      />
      <img
        class="pc-index__about-bg-icon pc-index__about-bg-icon--7"
        src="@/assets/image/index/about_bg_icon7.webp"
        alt=""
        aria-hidden="true"
        decoding="async"
        loading="lazy"
      />
      <img
        class="pc-index__about-bg-icon pc-index__about-bg-icon--8"
        src="@/assets/image/index/about_bg_icon8.webp"
        alt=""
        aria-hidden="true"
        decoding="async"
        loading="lazy"
      />
      <img
        class="pc-index__about-bg-icon pc-index__about-bg-icon--9"
        src="@/assets/image/index/about_bg_icon9.webp"
        alt=""
        aria-hidden="true"
        decoding="async"
        loading="lazy"
      />
      <img
        class="pc-index__about-bg-icon pc-index__about-bg-icon--10"
        src="@/assets/image/index/about_bg_icon10.webp"
        alt=""
        aria-hidden="true"
        decoding="async"
        loading="lazy"
      />
      <img
        class="pc-index__about-bg-icon pc-index__about-bg-icon--11"
        src="@/assets/image/index/about_bg_icon11.webp"
        alt=""
        aria-hidden="true"
        decoding="async"
        loading="lazy"
      />

      <img
        class="pc-index__about-wrap-title-img"
        src="@/assets/image/index/banner_title.webp"
        width="660"
        height="240"
        alt="Blue Archive 5th Anniversary DJ — title artwork"
        decoding="async"
        loading="lazy"
      />
      <div class="pc-index__about-wrap-title">
        {{ pcIndexCopy.about.titleLine }}
      </div>
      <div
        class="pc-index__about-wrap-content"
        v-html="pcIndexCopy.about.introHtml"
      />

      <!-- list -->
      <div class="pc-index__about-wrap-list">
        <a
          v-for="(item, index) in aboutTourList"
          :key="item.id"
          class="pc-index__about-wrap-list-item"
          :style="{ backgroundImage: `url(${cardBgUrls[index]})` }"
          :href="item.mapsUrl"
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="`${item.city} ${item.venue} — ${pcIndexCopy.about.mapLinkAriaSuffix}`"
        >
          <div class="pc-index__about-wrap-list-item-inner">
            <div class="pc-index__about-wrap-list-item-date-row">
              <span class="pc-index__about-wrap-list-item-date">
                <div class="pc-index__about-wrap-list-item-year">
                  {{ item.year }}
                </div>
                {{ item.dateDisplay }}</span
              >
              <span
                :class="
                  index !== 3
                    ? 'pc-index__about-wrap-list-item-dow'
                    : 'pc-index__about-wrap-list-item-dow-holiday'
                "
                >{{ item.dow }}</span
              >
            </div>
            <div class="pc-index__about-wrap-list-item-rows">
              <div
                v-for="slot in item.slots"
                :key="slot.label"
                class="pc-index__about-wrap-list-item-slot"
              >
                <span class="pc-index__about-wrap-list-item-badge">{{
                  slot.label
                }}</span>
                <div class="pc-index__about-wrap-list-item-times">
                  <span>
                    {{ pcIndexCopy.about.slotOpenLabel }} {{ slot.open }}
                  </span>
                  <span>
                    {{ pcIndexCopy.about.slotStartLabel }} {{ slot.start }}
                  </span>
                </div>
              </div>
            </div>
            <div class="pc-index__about-wrap-list-item-rows">
              <div class="pc-index__about-wrap-list-item-slot">
                <span class="pc-index__about-wrap-list-item-venue">
                  {{item.goodsSaleLabel}} : {{ item.goodsSaleTime }}
                  </span>
                </div>
            </div>
            <div class="pc-index__about-wrap-list-item-venue">
              会場 : {{ item.venue }}
            </div>
            <div
              class="pc-index__about-wrap-list-item-btn-wrap"
              aria-hidden="true"
            >
              <img
                class="pc-index__about-wrap-list-item-btn"
                :src="cardBtnUrls[index]"
                :alt="`${item.city} ${item.venue} — ${pcIndexCopy.about.mapLinkAltSuffix}`"
                decoding="async"
                loading="lazy"
              />
            </div>
          </div>
        </a>
      </div>

      <!-- 无限滚动横幅（与 banner 下 pc-index__banner-wrap-footer 同款） -->
      <div class="pc-index__about-wrap-footer">
        <div class="pc-index__about-wrap-footer-inner-notify">
          <div class="pc-index__about-wrap-footer-inner-notify-track">
            <div
              v-for="dup in 2"
              :key="'about-footer-marquee-' + dup"
              class="pc-index__about-wrap-footer-inner-notify-group"
              :aria-hidden="dup === 2 ? 'true' : undefined"
            >
              <img
                v-for="n in 10"
                :key="`about-footer-${dup}-${n}`"
                class="pc-index__about-wrap-footer-inner-notify-title"
                src="@/assets/image/index/about_footer_title.webp"
                width="314"
                height="30"
                alt="Scrolling footer title strip (decorative)"
                aria-hidden="true"
                decoding="async"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- dj（无限底纹已迁至 __dj-global-bg） -->
    <section ref="djWrap" class="pc-index__dj-wrap" aria-label="DJ">
      <div class="pc-index__dj-wrap-content">
        <img
          class="pc-index__dj-wrap-content-icon"
          src="@/assets/image/index/dj_icon.webp"
          width="250"
          height="250"
          alt="DJ section decorative icon"
          aria-hidden="true"
          decoding="async"
          loading="lazy"
        />
        <img
          class="pc-index__dj-wrap-content-title"
          src="@/assets/image/index/dj_title.webp"
          width="1000"
          height="120"
          alt="DJ section title graphic"
          decoding="async"
          loading="lazy"
        />
        <div class="pc-index__dj-wrap-content-list">
          <div
            v-for="(item, index) in djList"
            :key="index"
            class="pc-index__dj-wrap-content-list-item"
          >
            <img
              class="pc-index__dj-wrap-content-list-item-icon"
              :src="item.icon"
              width="200"
              height="200"
              :alt="`DJ portrait — ${item.name}`"
              decoding="async"
              loading="lazy"
            />
            <div class="pc-index__dj-wrap-content-list-item-name">
              {{ item.name }}
            </div>
          </div>
        </div>
        <div class="pc-index__dj-wrap-content-footer">
          {{ pcIndexCopy.dj.footerNote }}
        </div>
      </div>

      <!-- DJ：底部跑马灯（与 about-wrap-footer 同款） -->
      <div class="pc-index__dj-wrap-footer">
        <div class="pc-index__dj-wrap-footer-inner-notify">
          <div class="pc-index__dj-wrap-footer-inner-notify-track">
            <div
              v-for="dup in 2"
              :key="'dj-footer-marquee-' + dup"
              class="pc-index__dj-wrap-footer-inner-notify-group"
              :aria-hidden="dup === 2 ? 'true' : undefined"
            >
              <img
                v-for="n in 10"
                :key="`dj-footer-${dup}-${n}`"
                class="pc-index__dj-wrap-footer-inner-notify-title"
                src="@/assets/image/index/about_footer_title.webp"
                width="314"
                height="30"
                alt="Scrolling footer title strip (decorative)"
                aria-hidden="true"
                decoding="async"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ticket&streaming -->
    <section class="pc-index__ticket-wrap" aria-label="Ticketing and streaming">
      <img
        class="pc-index__ticket-wrap-title"
        :class="{ 'pc-index__ticket-wrap-title--compact': pcTicketWrapTitleCompact }"
        :src="pcTicketWrapTitleImg"
        :width="pcTicketWrapTitleCompact ? undefined : 1080"
        :height="pcTicketWrapTitleCompact ? 66 : 120"
        alt="Ticketing section title graphic"
        decoding="async"
        loading="lazy"
      />

      <div class="pc-index__ticket-wrap-content">
        <!-- 顶部卡片 -->
        <div class="pc-index__ticket-wrap-content-top-card">
          <div class="pc-index__ticket-wrap-content-top-card-brand">
            <img
              class="pc-index__ticket-wrap-content-top-card-logo"
              src="@/assets/image/logo/yostation.webp"
              width="100"
              height="100"
              alt="YoStation logo"
              decoding="async"
              loading="lazy"
            />
            <span class="pc-index__ticket-wrap-content-top-card-brand-name">{{
              pcIndexCopy.ticketYoStation.brandName
            }}</span>
          </div>
          <div class="pc-index__ticket-wrap-content-top-card-main">
            <div class="pc-index__ticket-wrap-content-top-card-capsule">
              <span
                class="pc-index__ticket-wrap-content-top-card-capsule-left"
                >{{ pcIndexCopy.ticketYoStation.capsuleLeft }}</span
              >
              <span
                class="pc-index__ticket-wrap-content-top-card-capsule-right"
              >
                {{ pcIndexCopy.ticketYoStation.capsuleRight }}
              </span>
            </div>
            <div class="pc-index__ticket-wrap-content-top-card-row2">
              <div class="pc-index__ticket-wrap-content-top-card-box">
                {{ pcIndexCopy.ticketYoStation.appBoxLabel }}
              </div>
              <div class="pc-index__ticket-wrap-content-top-card-box">
                {{ pcIndexCopy.ticketYoStation.contactBoxLabel }}
              </div>
            </div>
            <div class="pc-index__ticket-wrap-content-top-card-row3">
              <button
                type="button"
                class="pc-index__ticket-wrap-content-top-card-link pc-index__ticket-wrap-content-top-card-link--gold"
                @click="openTicketQrModal"
              >
                {{ pcIndexCopy.ticketYoStation.downloadCta }}
                <img
                  class="pc-index__ticket-wrap-content-top-card-link-icon"
                  src="@/assets/image/index/icon_open_other_1.webp"
                  width="14"
                  height="14"
                  alt=""
                  aria-hidden="true"
                  decoding="async"
                  loading="lazy"
                />
              </button>
              <a
                :href="pcIndexLinks.ticketInquiryForm"
                class="pc-index__ticket-wrap-content-top-card-link pc-index__ticket-wrap-content-top-card-link--cyan"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ pcIndexCopy.ticketYoStation.inquiryCta }}
                <img
                  class="pc-index__ticket-wrap-content-top-card-link-icon"
                  src="@/assets/image/index/icon_open_other_2.webp"
                  width="14"
                  height="14"
                  alt="External link icon — ticket inquiry contact form"
                  decoding="async"
                  loading="lazy"
                />
              </a>
            </div>
          </div>
        </div>

        <!-- 地址 / 场次 / 价格（3+2 栅格，与城市主题色一致） -->
        <div class="pc-index__ticket-wrap-content-address-list">
          <div
            v-for="item in ticketAddressList"
            :key="item.id"
            class="pc-index__ticket-wrap-content-address-list-item"
          >
            <img
              class="pc-index__ticket-wrap-content-address-list-logo"
              :src="item.logo"
              width="360"
              height="200"
              :alt="`Venue graphic — ${item.title}`"
              decoding="async"
              loading="lazy"
            />
            <div
              class="pc-index__ticket-wrap-content-address-list-panel"
              :style="{
                borderColor: item.color,
              }"
            >
              <div
                class="pc-index__ticket-wrap-content-address-list-heading"
                :style="{ color: item.color }"
              >
                {{ item.title }}
              </div>
              <div
                v-for="row in item.priceRows"
                :key="row.label"
                class="pc-index__ticket-wrap-content-address-list-price-row"
              >
                <span class="pc-index__ticket-wrap-content-address-list-label" >
                  <span>{{ row.num }}</span>
                  <span>{{ row.label }}</span>
                </span >
                <span class="pc-index__ticket-wrap-content-address-list-price">
                  {{ row.price }}
                  <span>{{ pcIndexCopy.ticket.priceTaxSuffix }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <router-link
          :to="pcIndexLinks.ticketPage"
          class="pc-index__ticket-wrap-content-more-link"
        >
          <span class="pc-index__ticket-wrap-content-more-stack">
            <img
              class="pc-index__ticket-wrap-content-more pc-index__ticket-wrap-content-more--base"
              src="@/assets/image/index/more_title_1.webp"
              width="1157"
              height="80"
              :alt="pcIndexCopy.imgAlt.ticketMore"
              decoding="async"
              loading="lazy"
            />
            <img
              class="pc-index__ticket-wrap-content-more pc-index__ticket-wrap-content-more--hover"
              src="@/assets/image/index/more_title_3.webp"
              width="1157"
              height="80"
              alt=""
              decoding="async"
              loading="lazy"
              aria-hidden="true"
            />
          </span>
        </router-link>
        <div
          v-if="showTicketWrapStreamingPromo"
          class="pc-index__ticket-wrap-content-message"
        >
          <div class="pc-index__ticket-wrap-content-message-title">
            {{ pcIndexCopy.streaming.blockTitle }}
          </div>
          <div class="pc-index__ticket-wrap-content-message-content">
            {{ pcIndexCopy.streaming.blockBody }}
          </div>
        </div>
        <router-link
          v-if="showTicketWrapStreamingPromo"
          :to="pcIndexLinks.streamingPage"
          class="pc-index__ticket-wrap-content-more-link"
        >
          <span class="pc-index__ticket-wrap-content-more-stack">
            <img
              class="pc-index__ticket-wrap-content-more pc-index__ticket-wrap-content-more--base"
              src="@/assets/image/index/more_title_2.webp"
              width="1157"
              height="80"
              :alt="pcIndexCopy.imgAlt.streamingMore"
              decoding="async"
              loading="lazy"
            />
            <img
              class="pc-index__ticket-wrap-content-more pc-index__ticket-wrap-content-more--hover"
              src="@/assets/image/index/more_title_3.webp"
              width="1157"
              height="80"
              alt=""
              decoding="async"
              loading="lazy"
              aria-hidden="true"
            />
          </span>
        </router-link>
      </div>
    </section>

    <!-- goods -->
    <section
      v-if="showHomeGoodsSection"
      class="pc-index__goods-wrap"
      aria-label="Goods"
    >
      <img
        class="pc-index__goods-wrap-title"
        src="@/assets/image/index/goods_title.webp"
        width="762"
        height="120"
        alt="Goods section title graphic"
        decoding="async"
        loading="lazy"
      />
      <img
        class="pc-index__goods-wrap-banner"
        src="@/assets/image/index/goods_banner.webp"
        width="1200"
        height="480"
        alt="Goods promotional banner"
        decoding="async"
        loading="lazy"
      />
      <router-link
        :to="pcIndexLinks.goodsPage"
        class="pc-index__goods-wrap-more-link"
      >
        <span class="pc-index__goods-wrap-more-stack">
          <img
            class="pc-index__goods-wrap-more-btn pc-index__goods-wrap-more-btn--base"
            src="@/assets/image/index/more_btn.webp"
            width="333"
            height="100"
            :alt="pcIndexCopy.imgAlt.goodsMore"
            decoding="async"
            loading="lazy"
          />
          <img
            class="pc-index__goods-wrap-more-btn pc-index__goods-wrap-more-btn--hover"
            src="@/assets/image/index/more_btn1.webp"
            width="333"
            height="100"
            alt=""
            decoding="async"
            loading="lazy"
            aria-hidden="true"
          />
        </span>
      </router-link>
    </section>

    <!-- YoStation 下载二维码全屏弹窗 -->
    <transition name="pc-ticket-qr-modal">
      <div
        v-show="ticketQrModalVisible"
        class="pc-index__ticket-qr-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pc-index-ticket-qr-modal-title"
        @click.self="closeTicketQrModal"
      >
        <h2
          id="pc-index-ticket-qr-modal-title"
          class="pc-index__ticket-qr-modal__sr-only"
        >
          {{ pcIndexCopy.ticketQrModal.dialogTitle }}
        </h2>
        <button
          type="button"
          class="pc-index__ticket-qr-modal__close"
          :aria-label="pcIndexCopy.ticketQrModal.closeLabel"
          @click="closeTicketQrModal"
        >
          <img
            src="@/assets/image/detail/close.webp"
            width="40"
            height="40"
            alt=""
            decoding="async"
          />
        </button>
        <div class="pc-index__ticket-qr-modal__inner">
          <div class="pc-index__ticket-qr-modal__qr-frame">
            <img
              class="pc-index__ticket-qr-modal__qr"
              src="@/assets/image/logo/qr.webp"
              width="360"
              height="360"
              :alt="pcIndexCopy.imgAlt.yoStationQr"
              decoding="async"
            />
          </div>
          <p class="pc-index__ticket-qr-modal__caption">
            {{ pcIndexCopy.ticketQrModal.caption }}
          </p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
/**
 * 运营维护入口：站内链接、外链、地图前缀与文案/列表见 @/views/data/index-content.json
 *（pcIndexCopy / pcIndexLinks 等；图片路径写在模板或本文件 require 常量中）
 */
import SimpleBar from "simplebar";
import "simplebar/dist/simplebar.css";
import indexContent from "@/views/data/index-content.json";
import contentVisibility from "@/views/data/content-visibility.json";

/** 票务场馆条 logo（顺序与 index-content.json ticketAddressList 一致） */
const PC_INDEX_TICKET_ADDRESS_LOGOS = [
  require("@/assets/image/index/address_logo_1.webp"),
  require("@/assets/image/index/address_logo_2.webp"),
  require("@/assets/image/index/address_logo_3.webp"),
  require("@/assets/image/index/address_logo_4.webp"),
  require("@/assets/image/index/address_logo_5.webp"),
];

/** DJ 头像（顺序与 index-content.json djList 一致） */
const PC_INDEX_DJ_ICONS = [
  require("@/assets/image/index/dj_team_1.webp"),
  require("@/assets/image/index/dj_team_2.webp"),
  require("@/assets/image/index/dj_team_3.webp"),
  require("@/assets/image/index/dj_team_4.webp"),
];

/** 与 PcHeader 固定顶栏高度一致（.pc-nav height: 56px） */
const PC_HEADER_SCROLL_OFFSET = 56;

/** banner-wrap 内图片迟迟未全部 load/error 时强制关闭首屏 Loading，避免卡死 */
const PAGE_LOADING_FALLBACK_MS = 8000;

/** nav name → 首页内滚动目标（本组件根内 querySelector；与 PcHeader NAV_ITEMS.name 一致） */
const NAV_NAME_SCROLL = {
  top: { kind: "top" },
  news: { kind: "selector", selector: ".pc-index__news-wrap" },
  about: { kind: "selector", selector: ".pc-index__about-wrap" },
  dj: { kind: "selector", selector: ".pc-index__dj-wrap" },
  ticket: { kind: "selector", selector: ".pc-index__ticket-wrap" },
  streaming: {
    kind: "selector",
    selector: ".pc-index__ticket-wrap-content-message",
  },
  goods: { kind: "selector", selector: ".pc-index__goods-wrap" },
  attention: { kind: "bottom" },
};

/** 顶栏滚动高亮顺序（与文档流一致；仅含首页内锚点项，与 PcHeader 无 routePath 项对应） */
const NAV_SCROLL_SPY_SEQUENCE = ["news", "about", "dj"];
/** 与顶栏内锚点 tab 一致（TOP / NEWS / ABOUT / DJ） */
const PC_NAV_ANCHOR_NAMES = ["top", ...NAV_SCROLL_SPY_SEQUENCE];

export default {
  name: "PcIndex",
  data() {
    return {
      // ========== 运营 / 页面配置（优先维护）==========
      /** 站内路由、外链、地图检索前缀（维护：index-content.json → pcIndexLinks） */
      pcIndexLinks: indexContent.pcIndexLinks,

      /** 可见文案与部分 img alt（维护：index-content.json） */
      pcIndexCopy: indexContent.pcIndexCopy,

      /**
       * 首页 NEWS（维护：index-content.json；接接口后可整体替换）
       * content 为富文本 HTML
       */
      newsList: indexContent.newsList,

      /**
       * ABOUT 巡演卡片（维护：index-content.json；顺序与 cardBgUrls / cardBtnUrls 一致）
       * JSON 内 mapsQuery 仅用于生成 mapsUrl（前缀见 pcIndexLinks.googleMapsSearchBase）
       */
      aboutTourList: indexContent.aboutTourList.map(({ mapsQuery, ...rest }) => ({
        ...rest,
        mapsUrl:
          indexContent.pcIndexLinks.googleMapsSearchBase +
          encodeURIComponent(mapsQuery),
      })),
      /** 巡演卡片背景图（与 aboutTourList 顺序一致） */
      cardBgUrls: [
        require("@/assets/image/index/card_bg_1.webp"),
        require("@/assets/image/index/card_bg_2.webp"),
        require("@/assets/image/index/card_bg_3.webp"),
        require("@/assets/image/index/card_bg_4.webp"),
        require("@/assets/image/index/card_bg_5.webp"),
      ],
      /** 巡演卡片「地图」按钮切图（与 aboutTourList 顺序一致） */
      cardBtnUrls: [
        require("@/assets/image/index/card_btn_1.webp"),
        require("@/assets/image/index/card_btn_2.webp"),
        require("@/assets/image/index/card_btn_3.webp"),
        require("@/assets/image/index/card_btn_4.webp"),
        require("@/assets/image/index/card_btn_5.webp"),
      ],

      /**
       * TICKET 场馆条（文案维护：index-content.json；logo 顺序同 JSON 条数）
       */
      ticketAddressList: indexContent.ticketAddressList.map((row, i) => ({
        ...row,
        logo: PC_INDEX_TICKET_ADDRESS_LOGOS[i],
      })),

      /** DJ 列表（姓名维护：index-content.json；头像顺序同 JSON 条数） */
      djList: indexContent.djList.map((row, i) => ({
        ...row,
        icon: PC_INDEX_DJ_ICONS[i],
      })),

      // ========== 运行时状态（一般无需改）==========
      /** 首屏 Loading：待 pc-index__banner-wrap 内图片就绪后关闭 */
      pageLoadingVisible: true,
      _pageLoadingFallbackTimer: null,
      /** YoStation 二维码全屏弹窗 */
      ticketQrModalVisible: false,
      /** SimpleBar 实例，beforeDestroy 里 unMount */
      newsSimpleBar: null,
      /** DJ 固定底纹：ABOUT / DJ 与视口相交（勿用 _ 前缀，Vue2 不代理到 this，计算属性会读不到） */
      djBgAboutInView: false,
      djBgDjInView: false,
      djBgAboutObserver: null,
      djBgDjObserver: null,
    };
  },

  computed: {
    /**
     * content-visibility.json → home.showTicketWrapMessageAndMoreLinks
     * 仅控制：配信文案块 + 第二条 MORE（配信页）；第一条票务 MORE 不受此字段影响
     */
    showTicketWrapStreamingPromo() {
      return (
        contentVisibility.home?.showTicketWrapMessageAndMoreLinks !==
        false
      );
    },
    /** content-visibility.json → home：首页 GOODS 整块（PC / H5 同源开关） */
    showHomeGoodsSection() {
      return contentVisibility.home?.showHomeGoodsSection !== false;
    },
    /**
     * content-visibility.json → header.showStreamingNav
     * true：含配信主题的标题图；false：仅票务标题图
     */
    pcTicketWrapTitleImg() {
      const showStreaming =
        contentVisibility.header?.showStreamingNav !== false;
      return showStreaming
        ? require("@/assets/image/index/ticket_title.webp")
        : require("@/assets/image/index/ticket_title1.webp");
    },
    /** false 时使用 ticket_title1，标题条高度 66px 居中 */
    pcTicketWrapTitleCompact() {
      return contentVisibility.header?.showStreamingNav === false;
    },
    /** ABOUT 或 DJ 任一在视口内则挂载固定底纹（ABOUT 段 opacity 0 预跑动画） */
    djGlobalBgLayerActive() {
      return this.djBgAboutInView || this.djBgDjInView;
    },
    /** DJ 区块进入视口后底纹渐显 */
    djGlobalBgLayerVisible() {
      return this.djBgDjInView;
    },
    /** NEWS 占位固定规则：0 条新闻显示 2 条，占 1 条新闻显示 1 条，其余不显示 */
    visibleNewsPlaceholderCount() {
      const len = (this.newsList && this.newsList.length) || 0;
      if (len <= 0) return 2;
      if (len === 1) return 1;
      return 0;
    },
  },

  mounted() {
    this.initPageLoadingBannerGate();
    this.initNewsSimpleBar();
    this.initNavScrollSpy();
    this.initDjGlobalBgObservers();
    this.$root.$on("pc-nav-scroll-to", this.scrollToNavName);
    this.consumePendingNavScroll();
  },
  beforeDestroy() {
    this.clearPageLoadingFallbackTimer();
    document.removeEventListener("keydown", this._onTicketQrModalKeydown);
    if (this.ticketQrModalVisible) {
      document.body.style.overflow = "";
    }
    this.teardownNavScrollSpy();
    this.teardownDjGlobalBgObservers();
    this.$root.$off("pc-nav-scroll-to", this.scrollToNavName);
    this.destroyNewsSimpleBar();
  },
  methods: {
    consumePendingNavScroll() {
      const pending = this.$root.__pcPendingNavScrollName;
      if (!pending) return;
      this.$root.__pcPendingNavScrollName = "";
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.scrollToNavName(pending);
        });
      });
    },
    clearPageLoadingFallbackTimer() {
      if (this._pageLoadingFallbackTimer != null) {
        clearTimeout(this._pageLoadingFallbackTimer);
        this._pageLoadingFallbackTimer = null;
      }
    },
    dismissPageLoading() {
      if (!this.pageLoadingVisible) return;
      this.pageLoadingVisible = false;
      this.clearPageLoadingFallbackTimer();
    },
    /** 统计 banner-wrap 内所有 img 的 load/error（已 complete 立即计入），齐后关 Loading */
    initPageLoadingBannerGate() {
      this._pageLoadingFallbackTimer = setTimeout(() => {
        this.dismissPageLoading();
      }, PAGE_LOADING_FALLBACK_MS);

      this.$nextTick(() => {
        const root = this.$refs.bannerWrap;
        if (!root) {
          this.dismissPageLoading();
          return;
        }
        const imgs = root.querySelectorAll("img");
        const total = imgs.length;
        if (total === 0) {
          this.dismissPageLoading();
          return;
        }
        let remaining = total;
        const onOne = () => {
          remaining -= 1;
          if (remaining <= 0) {
            this.dismissPageLoading();
          }
        };
        imgs.forEach((img) => {
          if (img.complete) {
            onOne();
          } else {
            img.addEventListener("load", onOne, { once: true });
            img.addEventListener("error", onOne, { once: true });
          }
        });
      });
    },
    openTicketQrModal() {
      document.removeEventListener("keydown", this._onTicketQrModalKeydown);
      document.addEventListener("keydown", this._onTicketQrModalKeydown);
      this.ticketQrModalVisible = true;
      document.body.style.overflow = "hidden";
    },
    closeTicketQrModal() {
      this.ticketQrModalVisible = false;
      document.body.style.overflow = "";
      document.removeEventListener("keydown", this._onTicketQrModalKeydown);
    },
    _onTicketQrModalKeydown(e) {
      if (e.key === "Escape") {
        this.closeTicketQrModal();
      }
    },
    initDjGlobalBgObservers() {
      if (typeof IntersectionObserver === "undefined") {
        return;
      }
      this.$nextTick(() => {
        const about = this.$refs.aboutWrap;
        const dj = this.$refs.djWrap;
        this.djBgAboutObserver = new IntersectionObserver(
          (entries) => {
            const e = entries[0];
            this.djBgAboutInView = !!(e && e.isIntersecting);
          },
          { root: null, threshold: 0, rootMargin: "0px" }
        );
        this.djBgDjObserver = new IntersectionObserver(
          (entries) => {
            const e = entries[0];
            this.djBgDjInView = !!(e && e.isIntersecting);
          },
          { root: null, threshold: 0, rootMargin: "0px" }
        );
        if (about) {
          this.djBgAboutObserver.observe(about);
        }
        if (dj) {
          this.djBgDjObserver.observe(dj);
        }
      });
    },
    teardownDjGlobalBgObservers() {
      if (this.djBgAboutObserver) {
        this.djBgAboutObserver.disconnect();
        this.djBgAboutObserver = null;
      }
      if (this.djBgDjObserver) {
        this.djBgDjObserver.disconnect();
        this.djBgDjObserver = null;
      }
    },
    initNavScrollSpy() {
      this._navScrollSpyRaf = null;
      this._navScrollSpyLastActive = null;
      this._onNavScrollSpyTick = () => {
        if (this._navScrollSpyRaf != null) return;
        this._navScrollSpyRaf = requestAnimationFrame(() => {
          this._navScrollSpyRaf = null;
          this.emitNavScrollSpyActive();
        });
      };
      window.addEventListener("scroll", this._onNavScrollSpyTick, {
        passive: true,
      });
      window.addEventListener("resize", this._onNavScrollSpyTick, {
        passive: true,
      });
      this.$nextTick(() => this.emitNavScrollSpyActive());
    },
    teardownNavScrollSpy() {
      if (this._onNavScrollSpyTick) {
        window.removeEventListener("scroll", this._onNavScrollSpyTick);
        window.removeEventListener("resize", this._onNavScrollSpyTick);
      }
      if (this._navScrollSpyRaf != null) {
        cancelAnimationFrame(this._navScrollSpyRaf);
        this._navScrollSpyRaf = null;
      }
      clearTimeout(this._scrollSpySuppressTimer);
      this._scrollSpySuppressTimer = null;
    },
    /** 点击顶栏触发的滚动期间暂停 spy，避免首帧 scrollY 仍为 0 把高亮打回 TOP */
    armScrollSpyAfterProgrammaticNav(name, behavior) {
      this._navScrollSpyLastActive = name;
      this.$root.$emit("pc-index-nav-scroll-spy", name);
      const ms = behavior === "smooth" ? 1100 : 100;
      this._scrollSpySuppressUntil = Date.now() + ms;
      clearTimeout(this._scrollSpySuppressTimer);
      this._scrollSpySuppressTimer = setTimeout(() => {
        this._scrollSpySuppressUntil = 0;
        this._scrollSpySuppressTimer = null;
        this.emitNavScrollSpyActive();
      }, ms);
    },
    /** 根据滚动位置同步顶栏锚点高亮（经 $root 通知 PcHeader） */
    emitNavScrollSpyActive() {
      if (
        this._scrollSpySuppressUntil &&
        Date.now() < this._scrollSpySuppressUntil
      ) {
        return;
      }
      const rootEl = this.$el;
      if (!rootEl) return;
      const scrollY =
        window.pageYOffset || document.documentElement.scrollTop || 0;
      let active = "top";
      for (const name of NAV_SCROLL_SPY_SEQUENCE) {
        const spec = NAV_NAME_SCROLL[name];
        if (!spec || spec.kind !== "selector" || !spec.selector) continue;
        const el = rootEl.querySelector(spec.selector);
        if (!el) continue;
        const elDocTop = el.getBoundingClientRect().top + scrollY;
        if (scrollY + 0.5 >= elDocTop - PC_HEADER_SCROLL_OFFSET) {
          active = name;
        }
      }
      if (active === this._navScrollSpyLastActive) return;
      this._navScrollSpyLastActive = active;
      this.$root.$emit("pc-index-nav-scroll-spy", active);
    },
    scrollBehavior() {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth";
    },
    /** 按顶栏项 name 滚动（由 PcHeader 发 pc-nav-scroll-to） */
    scrollToNavName(name) {
      let spec = NAV_NAME_SCROLL[name];
      if (
        name === "streaming" &&
        !this.showTicketWrapStreamingPromo &&
        NAV_NAME_SCROLL.ticket
      ) {
        spec = NAV_NAME_SCROLL.ticket;
      }
      if (
        name === "goods" &&
        !this.showHomeGoodsSection &&
        NAV_NAME_SCROLL.ticket
      ) {
        spec = NAV_NAME_SCROLL.ticket;
      }
      if (!spec) return;
      const behavior = this.scrollBehavior();
      if (PC_NAV_ANCHOR_NAMES.includes(name)) {
        this.armScrollSpyAfterProgrammaticNav(name, behavior);
      }
      if (spec.kind === "top") {
        window.scrollTo({ top: 0, behavior });
        return;
      }
      if (spec.kind === "bottom") {
        const root = document.documentElement;
        const top = Math.max(0, root.scrollHeight - root.clientHeight);
        window.scrollTo({ top, behavior });
        return;
      }
      const rootEl = this.$el;
      if (!rootEl || !spec.selector) return;
      const el = rootEl.querySelector(spec.selector);
      if (!el) return;
      const y =
        el.getBoundingClientRect().top +
        window.pageYOffset -
        PC_HEADER_SCROLL_OFFSET;
      window.scrollTo({ top: Math.max(0, y), behavior });
    },
    /** #RRGGBB → rgba(..., a)，用于主题色发光边框 */
    hexToRgba(hex, alpha) {
      let h = String(hex || "")
        .replace("#", "")
        .trim();
      if (h.length === 3) {
        h = h
          .split("")
          .map((c) => c + c)
          .join("");
      }
      if (h.length !== 6) return `rgba(255,255,255,${alpha})`;
      const n = parseInt(h, 16);
      const r = (n >> 16) & 255;
      const g = (n >> 8) & 255;
      const b = n & 255;
      return `rgba(${r},${g},${b},${alpha})`;
    },
    // 初始化新闻滚动条
    initNewsSimpleBar() {
      this.$nextTick(() => {
        const el = this.$refs.newsScrollRoot;
        if (!el) return;
        if (!this.newsSimpleBar) {
          this.newsSimpleBar = new SimpleBar(el, {
            autoHide: false,
            forceVisible: "y",
          });
        }
      });
    },

    // 销毁新闻滚动条
    destroyNewsSimpleBar() {
      if (this.newsSimpleBar) {
        this.newsSimpleBar.unMount();
        this.newsSimpleBar = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* 图片默认等比、防默认拉伸；各模块可覆盖 object-fit。字体：Google Fonts，display=swap（public/index.html） */
.pc-index {
  width: 100%;
  box-sizing: border-box;
  text-align: left;

  /* 主内容叠在固定层之上（__news-global-bg z0 → __dj-global-bg z1） */
  > .pc-index__banner-wrap,
  > .pc-index__news-wrap,
  > .pc-index__about-wrap,
  > .pc-index__ticket-wrap,
  > .pc-index__goods-wrap {
    position: relative;
    z-index: 2;
  }

  > .pc-index__dj-wrap {
    position: relative;
    z-index: 3;
  }

  img {
    box-sizing: border-box;
    object-fit: contain;
    object-position: center;
    vertical-align: middle;
  }
}

/* 主视觉区：banner、叠图、底部条均在 __banner-wrap 内 */
.pc-index__banner-wrap {
  position: relative;
  width: 100%;

  .pc-index__banner {
    display: block;
    width: 100%;
    aspect-ratio: 16 / 9;
    max-height: 1080px;
    object-fit: cover;
    object-position: center bottom;
    background-color: #2a2348;
  }

  .pc-index__overlay {
    position: absolute;
    width: auto;
    height: auto;
    object-fit: contain;
    pointer-events: none;
  }

  .pc-index__overlay--role {
    z-index: 1;
    left: 50%;
    top: 698px;
    transform: translate(-50%, 0);
    width: 1360px;
  }

  .pc-index__overlay--title {
    z-index: 2;
    left: 50%;
    top: 433px;
    transform: translate(-50%, 0);
    width: 660px;
  }

  &-footer {
    position: absolute;
    bottom: -67px;
    left: 0;
    width: 100%;
    height: 134px;
    background-image: url("@/assets/image/index/banner_footer_bg.webp");
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    z-index: 10;

    &-inner-notify {
      margin-top: 45px;
      overflow: hidden;
      background-color: #191616;
      height: 44px;
      display: flex;
      align-items: center;
      border-top: 1px solid #fff590;
      border-bottom: 1px solid #fff590;

      &-track {
        display: flex;
        width: max-content;
        flex-shrink: 0;
        align-items: center;
        animation: pc-index-notify-marquee 45s linear infinite;
        will-change: transform;

        @media (prefers-reduced-motion: reduce) {
          animation: none;
          will-change: auto;
        }
      }

      &-group {
        display: flex;
        flex-shrink: 0;
        align-items: center;
      }

      &-title {
        flex-shrink: 0;
        margin-right: 30px;
        width: 314px;
        height: 30px;
        object-fit: contain;
        object-position: center;
      }
    }
  }
}

/* NEWS 渐变 + 四角装饰：视口固定层内绝对定位（坐标相对视口） */
.pc-index__news-global-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(181, 152, 211, 0.6) 0%, rgba(60, 48, 104, 0.6) 100%);


  &__star-left {
    position: absolute;
    left: 113px;
    top: 121px;
    width: 180px;
    height: 180px;
    object-fit: contain;
    object-position: center;
    transform-origin: center center;
    animation: pc-index-star-pulse 1.5s ease-in-out infinite;
  }

  &__star-right {
    position: absolute;
    right: 197px;
    top: 467px;
    width: 100px;
    height: 100px;
    object-fit: contain;
    object-position: center;
    transform-origin: center center;
    animation: pc-index-star-pulse 1s ease-in-out infinite;
    animation-delay: 0.35s;
  }

  &__circular-left {
    position: absolute;
    left: 114px;
    top: 406px;
    width: 200px;
    height: 200px;
    object-fit: contain;
    object-position: center;
    transform-origin: center center;
    animation: pc-index-circular-spin 12s linear infinite;
  }

  &__circular-right {
    position: absolute;
    right: 86px;
    top: 121px;
    width: 260px;
    height: 260px;
    object-fit: contain;
    object-position: center;
    transform-origin: center center;
    animation: pc-index-circular-spin 4s linear infinite reverse;
  }

  @media (prefers-reduced-motion: reduce) {
    &__star-left,
    &__star-right,
    &__circular-left,
    &__circular-right {
      animation: none;
    }
  }
}

/* DJ 无限斜向滚动底纹：视口固定，叠在 __news-global-bg 上；可见性由脚本 + --visible 控制 */
.pc-index__dj-global-bg {
  position: fixed;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  overflow: hidden;
  /* 原 __dj-wrap 区块底色：铺满固定层，渐显时盖住 __news-global-bg 四角装饰 */
  background-color: #3c3068;
  opacity: 0;
  transition: opacity 0.45s ease;

  &--visible {
    opacity: 1;
  }

  &__layer {
    position: absolute;
    inset: 0;
    overflow: hidden;
  }

  &__track {
    position: absolute;
    left: -100vw;
    top: -100%;
    display: grid;
    grid-template-columns: repeat(3, 100vw);
    grid-template-rows: repeat(3, 1fr);
    width: 300vw;
    height: 300%;
    box-sizing: border-box;
    animation: pc-index-dj-bg-marquee 40s linear infinite;
    will-change: transform;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      will-change: auto;
    }
  }

  &__slab {
    min-height: 0;
    width: 100%;
    height: 100%;
    background-image: url("@/assets/image/index/dj_bg.webp");
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
}

// NEWS（无区块渐变；版心与标题/列表布局不变）
.pc-index__news-wrap {
  position: relative;
  width: 100%;
  min-height: 776px;
  padding-top: 1px;

  &-title {
    margin: 101px 0 0 820px;
    width: 697.3px;
    height: 67.5px;
    object-fit: contain;
    object-position: center;
  }

  /* 滚动由 SimpleBar 接管，勿在此写 overflow-y（见文末全局样式覆盖轨道/滑块） */
  &-list {
    margin: 38px 0 0 430px;
    width: 1050px;
    height: 460px;
    max-height: 460px;

    &-item {
      position: relative;
      width: 1000px;
      padding: 40px;
      margin-bottom: 28px;
      background-color: rgba(0, 0, 0, 0.25);
      border-radius: 10px;
      border: 1px solid #fff380;

      &-icon {
        position: absolute;
        top: 0;
        left: 0;
        width: 59px;
        height: 59px;
        object-fit: contain;
        object-position: center;
      }

      &-date {
        margin-bottom: 10px;
        color: #37fff1;
        font-size: 24px;
        font-weight: 700;
      }

      &-content {
        color: #fff;
        font-size: 16px;
        font-weight: 400;
        line-height: 1.5;

        /* v-html 内的节点无 data-v-*，scoped 需 ::v-deep 才能命中 a */
        &-text {
          ::v-deep a {
            font-weight: inherit;
            text-decoration: underline;
            text-underline-offset: 3px;
            transition: color 0.2s ease;

            &:hover {
              color: #fff380;
            }

            &:focus-visible {
              outline: 2px solid #37fff1;
              outline-offset: 2px;
              border-radius: 2px;
            }
          }
        }
      }

      /* 无数据或仅一条时：设计稿占位条 */
      &--coming-soon {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        flex-wrap: nowrap;
        box-sizing: border-box;
        width: 1000px;
        min-height: 134px;
        padding: 22px 40px;
        margin-top: 28px;
        margin-bottom: 28px;
        border: 1px solid #fff380;
        border-radius: 8px;
      }

      /* 相邻占位之间仅用上一条的 margin-bottom，避免 margin 叠加 */
      &--coming-soon + &--coming-soon {
        margin-top: 0;
      }

      &--coming-soon:last-child {
        margin-bottom: 0;
      }

      &-coming-en {
        flex-shrink: 0;
        font-size: 18px;
        font-weight: 700;
        letter-spacing: 0.04em;
        color: #898989;
        font-family: inherit;
      }

      &-coming-ja {
        flex-shrink: 0;
        margin-left: 20px;
        font-size: 18px;
        font-weight: 700;
        color: #fff;
        font-family: inherit;
      }
    }

    /* 真实新闻最后一条：去掉与下方 COMING SOON 或滚动区底部的多余间距 */
    &-real-items > div:last-child &-item {
      margin-bottom: 0;
    }
  }
}

// about
.pc-index__about-wrap {
  position: relative;
  width: 100%;
  min-height: 1186px;
  background-color: #12111A;
  background-image: url("@/assets/image/index/about_bg.webp");
  background-size: 100% 1013px;
  background-repeat: no-repeat;
  border-top: 2px solid #9f77bb;

  /* ABOUT 区散点装饰图（类名须与模板一致：pc-index__about-bg-icon） */
  .pc-index__about-bg-icon {
    position: absolute;
    z-index: 0;
    pointer-events: none;
    display: block;
    width: auto;
    height: auto;
    transform: translate3d(0, 0, 0);
    animation: pc-index-about-bg-icon-float 5s ease-in-out infinite;
    will-change: transform;

    /* 1–6 左；7–11 右；animation-delay 错开相位 */
    &--1 {
      width: 181px;
      height: 187px;
      top: 65px;
      left: 265px;
      animation-delay: 0s;
    }
    &--2 {
      width: 140px;
      height: 140px;
      top: 234px;
      left: 80px;
      animation-delay: 0.65s;
    }
    &--3 {
      width: 220px;
      height: 220px;
      top: 305px;
      left: 210px;
      animation-delay: 1.3s;
    }
    &--4 {
      width: 200px;
      height: 200px;
      top: 204px;
      left: 425px;
      animation-delay: 1.95s;
    }
    &--5 {
      width: 150px;
      height: 150px;
      top: 471px;
      left: 462px;
      animation-delay: 2.6s;
    }
    &--6 {
      width: 200px;
      height: 200px;
      top: 527px;
      left: 52px;
      animation-delay: 3.25s;
    }
    &--7 {
      width: 230px;
      height: 230px;
      top: 109px;
      left: auto;
      right: 359px;
      animation-delay: 3.9s;
    }
    &--8 {
      width: 140px;
      height: 140px;
      top: 133px;
      left: auto;
      right: 106px;
      animation-delay: 4.55s;
    }
    &--9 {
      width: 420px;
      height: 276px;
      top: 227px;
      left: auto;
      right: 73px;
      animation-delay: 5.2s;
    }
    &--10 {
      width: 180px;
      height: 180px;
      top: 478px;
      left: auto;
      right: 475px;
      animation-delay: 5.85s;
    }
    &--11 {
      width: 200px;
      height: 200px;
      top: 552px;
      left: auto;
      right: 26px;
      animation-delay: 6.5s;
    }
  }

  &-title-img {
    position: absolute;
    z-index: 1;
    left: 50%;
    top: 110px;
    transform: translate(-50%, 0);
    width: 660px;
    object-fit: contain;
    object-position: center;
  }

  &-title {
    padding-top: 440px;
    color: #37ffef;
    font-size: 28px;
    font-weight: 700;
    text-align: center;
  }

  &-content {
    padding-top: 30px;
    color: #fff;
    font-size: 16px;
    font-weight: 400;
    line-height: 2;
    text-align: center;
  }

  &-list {
    padding: 50px 280px 100px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;

    &-item {
      position: relative;
      z-index: 0;
      margin: 0 13px 20px;
      width: 240px;
      height: 347px;
      box-sizing: border-box;
      background-size: 100% 100%;
      transform-origin: center center;
      cursor: pointer;
      text-decoration: none;
      color: inherit;
      transition: transform 0.32s cubic-bezier(0.34, 1.25, 0.64, 1),
        filter 0.26s ease, box-shadow 0.32s ease;

      &:hover {
        z-index: 2;
        transform: translateY(-8px) scale(1.03);
        filter: brightness(1.06) saturate(1.05);
      }

      &:active {
        transform: translateY(-3px) scale(1.01);
        filter: brightness(0.97);
        transition-duration: 0.08s;
      }

      &-inner {
        display: flex;
        flex-direction: column;
        height: 100%;
        padding-bottom: 18px;
        box-sizing: border-box;
      }

      &-year {
        position: absolute;
        top: -16px;
        left: 0;
        font-size: 12px;
        color: #fff;
        line-height: 1.2;
        font-weight: 400;
      }

      &-date-row {
        margin-top: 96px;
        display: flex;
        flex-wrap: wrap;
        align-items: flex-end;
        justify-content: center;
        gap: 6px 10px;
        margin-bottom: 18px;
      }

      &-date {
        position: relative;
        font-size: 40px;
        font-weight: 700;
        color: #fff;
        line-height: 1;
        letter-spacing: 0.02em;
      }

      &-dow {
        font-size: 16px;
        font-weight: 700;
        color: #36e9ff;
      }
      &-dow-holiday {
        font-size: 16px;
        font-weight: 700;
        color: #ff835d;
      }

      &-rows {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        height: 46px;
      }

      &-slot {
        margin-bottom: 4px;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      &-badge {
        flex-shrink: 0;
        min-width: 22px;
        height: 14px;
        line-height: 14px;
        border-radius: 2px;
        background-color: #4ec4fa;
        color: #000;
        font-size: 12px;
        font-weight: 700;
        text-align: center;
      }

      &-times {
        flex: 1;
        display: flex;
        font-size: 14px;
        color: #fff;
        line-height: 1.35;

        span {
          margin: 0 4px;
        }
      }

      &-venue {
        margin-top: 4px;
        font-size: 14px;
        font-weight: 700;
        color: #fff;
        line-height: 1.35;
        text-align: center;
      }

      &-btn-link {
        margin: 20px auto 0;
        width: 80%;
        display: block;
        line-height: 0;
        text-decoration: none;
        color: inherit;
        border-radius: 4px;
      }

      &-btn-wrap {
        position: absolute;
        bottom: 32px;
        left: 50%;
        transform: translateX(-50%);
        width: 80%;
        max-width: 320px;
        display: block;
        line-height: 0;
      }

      &-btn {
        width: 100%;
        height: auto;
        display: block;
        object-fit: contain;
        object-position: center;
        transform-origin: center center;
        transition: transform 0.24s cubic-bezier(0.34, 1.56, 0.64, 1),
          filter 0.2s ease;
      }

      &:hover &-btn {
        transform: scale(1.1);
        filter: brightness(1.14) drop-shadow(0 4px 16px rgba(54, 233, 255, 0.5));
      }

      &-btn-wrap:active &-btn {
        transform: scale(0.94);
        filter: brightness(0.88);
        transition-duration: 0.08s;
      }
    }
  }

  /* 与 __banner-wrap-footer 相同的跑马灯，放在 ABOUT 区块底部（文档流内，非叠在 banner 上） */
  &-footer {
    position: absolute;
    bottom: -67px;
    left: 0;
    width: 100%;
    height: 134px;
    padding-top: 1px;
    background-image: url("@/assets/image/index/about_footer_bg.webp");
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    z-index: 1;

    &-inner-notify {
      margin-top: 45px;
      overflow: hidden;
      background-color: #191616;
      height: 44px;
      display: flex;
      align-items: center;
      border-top: 1px solid #d9a2ff;
      border-bottom: 1px solid #d9a2ff;

      &-track {
        display: flex;
        width: max-content;
        flex-shrink: 0;
        align-items: center;
        animation: pc-index-notify-marquee 45s linear infinite;
        will-change: transform;

        @media (prefers-reduced-motion: reduce) {
          animation: none;
          will-change: auto;
        }
      }

      &-group {
        display: flex;
        flex-shrink: 0;
        align-items: center;
      }

      &-title {
        flex-shrink: 0;
        margin-right: 30px;
        width: 314px;
        height: 30px;
        object-fit: contain;
        object-position: center;
      }
    }
  }
}

// DJ（无限底纹在 __dj-global-bg；此处背景须透明，否则会盖住 z-index 更低的固定滚动层）
.pc-index__dj-wrap {
  position: relative;
  z-index: 3;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 918px;
  background-color: transparent;

  &-content {
    position: relative;
    margin: 139px auto;
    width: 1200px;
    height: 640px;
    background-color: rgba(18, 17, 26, 0.7);
    border-top: 2px solid #d9a2ff;
    border-bottom: 2px solid #d9a2ff;
    border-radius: 4px;

    &-icon {
      position: absolute;
      top: -55px;
      left: -129px;
      width: 250px;
      height: 250px;
      object-fit: contain;
      object-position: center;
    }

    &-title {
      margin: 101px 0 0 122px;
      width: 1000px;
      object-fit: contain;
      object-position: center;
    }

    &-list {
      display: flex;
      justify-content: center;
      align-items: center;

      &-item {
        margin: 70px 20px;

        &-icon {
          width: 200px;
          height: 200px;
          object-fit: contain;
          object-position: center;
        }

        &-name {
          margin-top: 30px;
          padding: 4px 20px;
          text-align: center;
          font-size: 16px;
          font-weight: 700;
          color: #fff;
          border: 1px solid #fff380;
          border-radius: 32px;
        }
      }
    }

    &-footer {
      position: absolute;
      bottom: 18px;
      right: 18px;
      font-size: 12px;
      color: #a6a6a6;
      font-weight: 700;
    }
  }

  /* 与 pc-index__about-wrap-footer 同款，贴区块底部 */
  &-footer {
    position: absolute;
    bottom: -67px;
    left: 0;
    z-index: 1;
    flex-shrink: 0;
    margin-top: auto;
    width: 100%;
    height: 134px;
    padding-top: 1px;
    background-image: url("@/assets/image/index/about_footer_bg.webp");
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;

    &-inner-notify {
      margin-top: 45px;
      overflow: hidden;
      background-color: #191616;
      height: 44px;
      display: flex;
      align-items: center;
      border-top: 1px solid #d9a2ff;
      border-bottom: 1px solid #d9a2ff;

      &-track {
        display: flex;
        width: max-content;
        flex-shrink: 0;
        align-items: center;
        animation: pc-index-notify-marquee 45s linear infinite;
        will-change: transform;

        @media (prefers-reduced-motion: reduce) {
          animation: none;
          will-change: auto;
        }
      }

      &-group {
        display: flex;
        flex-shrink: 0;
        align-items: center;
      }

      &-title {
        flex-shrink: 0;
        margin-right: 30px;
        width: 314px;
        height: 30px;
        object-fit: contain;
        object-position: center;
      }
    }
  }
}

// ticket&streaming
.pc-index__ticket-wrap {
  padding: 1px 0 150px;
  position: relative;
  width: 100%;
  background-color: #111019;
  background-image: url("@/assets/image/index/about_bg.webp");
  background-size: 100% 1013px;
  background-repeat: no-repeat;
  border-bottom: 2px solid #9f77bb;

  &-title {
    margin: 200px auto 0;
    width: 1080px;
    height: auto;
    object-fit: contain;
    object-position: center;
    display: block;

    &--compact {
      width: auto;
      max-width: 100%;
      height: 66px;
    }
  }

  &-content {
    margin: 85px auto 0;
    width: 1200px;

    &-top-card {
      display: flex;
      align-items: stretch;
      justify-content: center;
      gap: 48px;
      padding: 44px 56px;
      border: 1px solid #fff380;
      border-radius: 8px;
      box-sizing: border-box;
      background-color: rgba(5, 5, 16, 0.6);

      &-brand {
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 160px;
        gap: 12px;
      }

      &-logo {
        display: block;
        width: 100px;
        height: 100px;
        object-fit: contain;
        object-position: center;
      }

      &-brand-name {
        font-size: 18px;
        font-weight: 700;
        color: #fff;
        letter-spacing: 0.02em;
      }

      &-main {
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 20px;
        justify-content: center;
      }

      &-capsule {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;
        gap: 12px 24px;
        padding: 10px 20px;
      }

      &-capsule-left,
      &-capsule-right {
        font-size: 14px;
        font-weight: 700;
        color: #36e9ff;
        line-height: 1.4;
        margin: 0 20px;
      }

      &-row2 {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
      }

      &-box {
        padding: 4px 28px;
        margin: 0 20px;
        text-align: center;
        font-size: 14px;
        font-weight: 600;
        color: #fff;
        line-height: 1.45;
        border: 1px solid rgba(255, 255, 255, 0.85);
        border-radius: 4px;
        box-sizing: border-box;
      }

      &-row3 {
        margin-top: 20px;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
        align-items: flex-end;
      }

      &-link {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        font-size: 14px;
        font-weight: 700;
        text-decoration: none;
        padding-bottom: 6px;
        margin: 0 20px;
        border-bottom-width: 1px;
        border-bottom-style: solid;
        transition: opacity 0.2s ease, filter 0.2s ease;

        &[type="button"] {
          font: inherit;
          color: inherit;
          background: transparent;
          border: none;
          border-bottom-width: 1px;
          border-bottom-style: solid;
          border-bottom-color: currentColor;
          cursor: pointer;
          appearance: none;
          -webkit-appearance: none;
          padding: 0 0 6px;
          margin: 0 20px;
          text-align: inherit;
        }

        &:hover {
          opacity: 0.88;
          filter: brightness(1.08);
        }

        &:active {
          opacity: 0.95;
          filter: brightness(0.92);
        }

        &:focus-visible {
          outline: 2px solid currentColor;
          outline-offset: 3px;
          border-radius: 2px;
        }

        &-icon {
          flex-shrink: 0;
          display: block;
          width: 14px;
          height: 14px;
          object-fit: contain;
          object-position: center;
        }

        &--gold {
          color: #fff380 !important;
          border-bottom-color: #fff380;
        }

        &--cyan {
          color: #36e9ff;
          border-bottom-color: #36e9ff;
        }
      }
    }

    &-address-list {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 28px 80px;
      margin-top: 20px;
      padding-bottom: 60px;
    }

    &-address-list-item {
      width: 320px;
      margin: 30px 0 0;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    &-address-list-logo {
      display: block;
      width: 100%;
      height: auto;
      object-fit: contain;
      object-position: center bottom;
      margin-bottom: 20px;
    }

    &-address-list-panel {
      width: 100%;
      padding: 38px 30px 38px;
      border-width: 1px;
      border-style: solid;
      border-radius: 4px;
      box-sizing: border-box;
      background: #1d1c25;
    }

    &-address-list-heading {
      font-size: 14px;
      font-weight: 700;
      line-height: 1.4;
      margin-bottom: 4px;
    }

    &-address-list-price-row {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 16px;
      font-size: 14px;
      line-height: 1.5;
      color: #fff;
    }

    &-address-list-label {
      flex: 1;
      min-width: 0;
      font-weight: 600;

      span {
        padding-right: 15px;
      }
    }

    &-address-list-price {
      flex-shrink: 0;
      font-weight: 700;
      text-align: right;
      font-size: 14px;
      white-space: nowrap;

      span {
        font-size: 12px;
        font-weight: 400;
      }
    }

    &-more-link {
      display: block;
      width: 1157px;
      max-width: 100%;
      margin: 60px auto 0;
      line-height: 0;
      text-decoration: none;
      color: inherit;
      border-radius: 4px;
      cursor: pointer;
      transform-origin: center center;
      transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1),
        filter 0.2s ease;

      &:hover .pc-index__ticket-wrap-content-more-stack,
      &:focus-visible .pc-index__ticket-wrap-content-more-stack {
        transform: scale(1.04);
        filter: brightness(1.1)
          drop-shadow(0 6px 18px rgba(255, 243, 128, 0.25));
      }

      &:active .pc-index__ticket-wrap-content-more-stack {
        transform: scale(0.96);
        filter: brightness(0.9);
        transition-duration: 0.08s;
      }

      &:hover .pc-index__ticket-wrap-content-more--hover,
      &:focus-visible .pc-index__ticket-wrap-content-more--hover {
        clip-path: inset(0 0 0 0);
      }

      &:focus-visible {
        outline: 2px solid #fff380;
        outline-offset: 6px;
      }
    }

    &-more-stack {
      position: relative;
      display: block;
      width: 100%;
      transform-origin: center center;
      transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1),
        filter 0.2s ease;
    }

    &-more {
      display: block;
      width: 100%;
      height: auto;
      object-fit: contain;
      object-position: center;
      margin: 0;
      transform-origin: center center;
      transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1),
        filter 0.2s ease;
      pointer-events: none;
    }

    &-more--base {
      position: relative;
      z-index: 0;
    }

    &-more--hover {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      z-index: 1;
      clip-path: inset(0 0 0 100%);
      transition: clip-path 0.45s cubic-bezier(0.4, 0, 0.2, 1);
    }

    &-message {
      margin: 60px auto 0;
      padding: 40px 60px;
      background-color: #0d0d13;
      border-radius: 8px;
      border: 1px solid #d9a2ff;

      &-title {
        color: #fff380;
        font-size: 24px;
        font-weight: 700;
      }

      &-content {
        margin-top: 20px;
        color: #fff;
        font-size: 16px;
        font-weight: 400;
        line-height: 1.5;
      }
    }
  }
}

// goods
.pc-index__goods-wrap {
  padding: 153px 357px 119px;
  position: relative;
  width: 100%;
  min-height: 1012px;
  background-color: #fff48b;
  background-image: url("@/assets/image/index/goods_bg.webp");
  background-size: 100% 100%;
  background-repeat: no-repeat;
  border-bottom: 2px solid #9f77bb;

  &-title {
    width: 762px;
    object-fit: contain;
    object-position: center;
  }

  &-banner {
    margin-top: 67px;
    width: 1200px;
    object-fit: contain;
    object-position: center;
  }

  &-more-link {
    position: absolute;
    bottom: 120px;
    right: 360px;
    line-height: 0;
    text-decoration: none;
    color: inherit;
    border-radius: 4px;
    cursor: pointer;
    transform-origin: center center;
    transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1),
      filter 0.2s ease;

    &:hover .pc-index__goods-wrap-more-stack,
    &:focus-visible .pc-index__goods-wrap-more-stack {
      transform: scale(1.04);
      filter: brightness(1.08) drop-shadow(0 6px 16px rgba(159, 119, 187, 0.45));
    }

    &:active .pc-index__goods-wrap-more-stack {
      transform: scale(0.96);
      filter: brightness(0.9);
      transition-duration: 0.08s;
    }

    &:hover .pc-index__goods-wrap-more-btn--hover,
    &:focus-visible .pc-index__goods-wrap-more-btn--hover {
      clip-path: inset(0 0 0 0);
    }

    &:focus-visible {
      outline: 2px solid #9f77bb;
      outline-offset: 4px;
    }
  }

  &-more-stack {
    position: relative;
    display: block;
    width: 333px;
    max-width: calc(100% - 40px);
    transform-origin: center center;
    transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1),
      filter 0.2s ease;
  }

  &-more-btn {
    display: block;
    width: 100%;
    height: auto;
    object-fit: contain;
    object-position: center;
    margin: 0;
    transform-origin: center center;
    transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1),
      filter 0.2s ease;
    pointer-events: none;
  }

  &-more-btn--base {
    position: relative;
    z-index: 0;
  }

  &-more-btn--hover {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    clip-path: inset(0 0 0 100%);
    transition: clip-path 0.45s cubic-bezier(0.4, 0, 0.2, 1);
  }
}

/* 首页首屏 Loading：视觉与 ticket-qr-modal 全屏层一致，中间为标题图（关闭逻辑未接） */
.pc-index__page-loading {
  position: fixed;
  inset: 0;
  z-index: 250;
  background-color: #35353c;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 72px 24px 32px;
  box-sizing: border-box;
  border-top: 2px solid #c84cff;
  border-bottom: 2px solid #c84cff;
  box-shadow: 0 -8px 22px -2px rgba(191, 0, 255, 0.55),
    0 8px 22px -2px rgba(191, 0, 255, 0.55),
    0 -18px 40px -6px rgba(200, 76, 255, 0.35),
    0 18px 40px -6px rgba(200, 76, 255, 0.35);

  &__inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    max-width: 100%;
  }

  /* 与 ticket 页 .pc-ticket__deco--left 相同：pc-ticket-deco-beat */
  &__title {
    display: block;
    width: min(660px, 92vw);
    max-width: 100%;
    height: auto;
    object-fit: contain;
    object-position: center;
    transform-origin: center center;
    will-change: transform;
    animation: pc-index-page-loading-beat 1.35s infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &__title {
      animation: none !important;
    }
  }
}

/* YoStation 下载 QR 全屏弹窗（金色「ダウンロードはこちら」） */
.pc-index__ticket-qr-modal {
  position: fixed;
  inset: 0;
  z-index: 301;
  background-color: #35353c;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 72px 24px 32px;
  box-sizing: border-box;
  border-top: 2px solid #c84cff;
  border-bottom: 2px solid #c84cff;
  box-shadow: 0 -8px 22px -2px rgba(191, 0, 255, 0.55),
    0 8px 22px -2px rgba(191, 0, 255, 0.55),
    0 -18px 40px -6px rgba(200, 76, 255, 0.35),
    0 18px 40px -6px rgba(200, 76, 255, 0.35);

  &__sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  &__close {
    position: absolute;
    top: 24px;
    right: 24px;
    width: 40px;
    height: 40px;
    padding: 0;
    margin: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    border-radius: 50%;
    transform-origin: center center;
    -webkit-tap-highlight-color: transparent;
    transition: transform 0.24s cubic-bezier(0.34, 1.45, 0.64, 1),
      filter 0.2s ease, background-color 0.2s ease, box-shadow 0.24s ease;

    img {
      display: block;
      width: 40px;
      height: 40px;
      object-fit: contain;
      pointer-events: none;
      transition: transform 0.24s cubic-bezier(0.34, 1.45, 0.64, 1),
        filter 0.2s ease;
    }

    &:hover {
      transform: scale(1.12);
      filter: brightness(1.12) drop-shadow(0 4px 12px rgba(255, 243, 128, 0.35));

      img {
        transform: rotate(90deg);
      }
    }

    &:active {
      transform: scale(0.9);
      filter: brightness(0.9);
      transition-duration: 0.08s;

      img {
        transform: rotate(90deg) scale(0.95);
        transition-duration: 0.08s;
      }
    }

    &:focus-visible {
      outline: 2px solid #fff380;
      outline-offset: 2px;
    }
  }

  &__inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    max-width: 100%;
  }

  &__qr-frame {
    background-color: #fff;
    padding: 20px;
    box-sizing: border-box;
    line-height: 0;
    max-width: 100%;
    border-radius: 8px;
    overflow: hidden;
  }

  &__qr {
    display: block;
    width: 360px;
    height: 360px;
    max-width: 100%;
    max-height: min(360px, 55vh);
    object-fit: contain;
  }

  &__caption {
    margin: 24px 0 0;
    padding: 0;
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.5;
    text-align: center;
  }
}

/* 弹窗进出场（与 <transition name="pc-ticket-qr-modal"> 配套） */
.pc-index__ticket-qr-modal.pc-ticket-qr-modal-enter-active,
.pc-index__ticket-qr-modal.pc-ticket-qr-modal-leave-active {
  transition: opacity 0.32s cubic-bezier(0.4, 0, 0.2, 1);
}

.pc-index__ticket-qr-modal.pc-ticket-qr-modal-enter-active
  .pc-index__ticket-qr-modal__inner,
.pc-index__ticket-qr-modal.pc-ticket-qr-modal-leave-active
  .pc-index__ticket-qr-modal__inner {
  transition: transform 0.4s cubic-bezier(0.34, 1.18, 0.64, 1),
    opacity 0.32s ease;
}

.pc-index__ticket-qr-modal.pc-ticket-qr-modal-enter,
.pc-index__ticket-qr-modal.pc-ticket-qr-modal-leave-to {
  opacity: 0;
}

.pc-index__ticket-qr-modal.pc-ticket-qr-modal-enter
  .pc-index__ticket-qr-modal__inner,
.pc-index__ticket-qr-modal.pc-ticket-qr-modal-leave-to
  .pc-index__ticket-qr-modal__inner {
  opacity: 0;
  transform: scale(0.92) translateY(20px);
}

@media (prefers-reduced-motion: reduce) {
  .pc-index__ticket-qr-modal.pc-ticket-qr-modal-enter-active,
  .pc-index__ticket-qr-modal.pc-ticket-qr-modal-leave-active {
    transition: none !important;
  }

  .pc-index__ticket-qr-modal.pc-ticket-qr-modal-enter-active
    .pc-index__ticket-qr-modal__inner,
  .pc-index__ticket-qr-modal.pc-ticket-qr-modal-leave-active
    .pc-index__ticket-qr-modal__inner {
    transition: none !important;
  }

  .pc-index__ticket-qr-modal.pc-ticket-qr-modal-enter
    .pc-index__ticket-qr-modal__inner,
  .pc-index__ticket-qr-modal.pc-ticket-qr-modal-leave-to
    .pc-index__ticket-qr-modal__inner {
    transform: none !important;
    opacity: 1 !important;
  }

  .pc-index__ticket-qr-modal__close,
  .pc-index__ticket-qr-modal__close img {
    transition: none !important;
  }

  .pc-index__ticket-qr-modal__close:hover,
  .pc-index__ticket-qr-modal__close:active {
    transform: none !important;
    filter: none !important;
  }

  .pc-index__ticket-qr-modal__close:hover img,
  .pc-index__ticket-qr-modal__close:active img {
    transform: none !important;
  }

  .pc-index__about-wrap-footer-inner-notify-track {
    animation: none !important;
    will-change: auto !important;
  }

  .pc-index__about-bg-icon {
    animation: none !important;
    will-change: auto !important;
  }

  .pc-index__dj-global-bg__track {
    animation: none !important;
    will-change: auto !important;
  }

  .pc-index__dj-global-bg {
    transition: none !important;
  }

  .pc-index__dj-wrap-footer-inner-notify-track {
    animation: none !important;
    will-change: auto !important;
  }

  .pc-index__about-wrap-list-item {
    transition: none !important;

    &:hover,
    &:active {
      transform: none !important;
      filter: none !important;
      box-shadow: none !important;
    }
  }

  .pc-index__about-wrap-list-item-btn-link {
    transition: none !important;
  }

  .pc-index__about-wrap-list-item-btn {
    transition: none !important;
  }

  .pc-index__about-wrap-list-item:hover .pc-index__about-wrap-list-item-btn,
  .pc-index__about-wrap-list-item-btn-link:active
    .pc-index__about-wrap-list-item-btn {
    transform: none !important;
    filter: none !important;
  }

  .pc-index__ticket-wrap-content-more-link {
    transition: none !important;
  }

  .pc-index__ticket-wrap-content-more {
    transition: none !important;
  }

  .pc-index__ticket-wrap-content-more-link:hover
    .pc-index__ticket-wrap-content-more,
  .pc-index__ticket-wrap-content-more-link:active
    .pc-index__ticket-wrap-content-more,
  .pc-index__ticket-wrap-content-more-link:focus-visible
    .pc-index__ticket-wrap-content-more,
  .pc-index__ticket-wrap-content-more-link:hover
    .pc-index__ticket-wrap-content-more-stack,
  .pc-index__ticket-wrap-content-more-link:active
    .pc-index__ticket-wrap-content-more-stack,
  .pc-index__ticket-wrap-content-more-link:focus-visible
    .pc-index__ticket-wrap-content-more-stack {
    transform: none !important;
    filter: none !important;
  }

  .pc-index__ticket-wrap-content-more-link:hover
    .pc-index__ticket-wrap-content-more--hover,
  .pc-index__ticket-wrap-content-more-link:active
    .pc-index__ticket-wrap-content-more--hover,
  .pc-index__ticket-wrap-content-more-link:focus-visible
    .pc-index__ticket-wrap-content-more--hover {
    clip-path: inset(0 0 0 0) !important;
    transition: none !important;
  }

  .pc-index__goods-wrap-more-link {
    transition: none !important;
  }

  .pc-index__goods-wrap-more-btn {
    transition: none !important;
  }

  .pc-index__goods-wrap-more-stack {
    transition: none !important;
  }

  .pc-index__goods-wrap-more-link:hover .pc-index__goods-wrap-more-stack,
  .pc-index__goods-wrap-more-link:active .pc-index__goods-wrap-more-stack,
  .pc-index__goods-wrap-more-link:focus-visible
    .pc-index__goods-wrap-more-stack {
    transform: none !important;
    filter: none !important;
  }

  .pc-index__goods-wrap-more-link:hover .pc-index__goods-wrap-more-btn,
  .pc-index__goods-wrap-more-link:active .pc-index__goods-wrap-more-btn,
  .pc-index__goods-wrap-more-link:focus-visible .pc-index__goods-wrap-more-btn {
    transform: none !important;
    filter: none !important;
  }

  .pc-index__goods-wrap-more-link:hover .pc-index__goods-wrap-more-btn--hover,
  .pc-index__goods-wrap-more-link:active .pc-index__goods-wrap-more-btn--hover,
  .pc-index__goods-wrap-more-link:focus-visible
    .pc-index__goods-wrap-more-btn--hover {
    clip-path: inset(0 0 0 0) !important;
    transition: none !important;
  }
}

// 无限滚动横幅动画
/* 与 src/views/pc/ticket/index.vue @keyframes pc-ticket-deco-beat 一致 */
@keyframes pc-index-page-loading-beat {
  0%,
  42% {
    transform: scale(1);
    animation-timing-function: cubic-bezier(0.45, 0, 0.55, 1);
  }
  52% {
    transform: scale(0.9);
    animation-timing-function: cubic-bezier(0.55, 0.05, 0.6, 0.45);
  }
  58% {
    transform: scale(0.86);
    animation-timing-function: cubic-bezier(0.33, 1.15, 0.52, 1);
  }
  70% {
    transform: scale(1.08);
    animation-timing-function: cubic-bezier(0.22, 0.85, 0.38, 1);
  }
  80% {
    transform: scale(1);
    animation-timing-function: cubic-bezier(0.25, 0.1, 0.25, 1);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes pc-index-notify-marquee {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

@keyframes pc-index-star-pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

@keyframes pc-index-circular-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* ABOUT 散点 icon 浮动（须与 .pc-index__about-bg-icon 的 animation 同名） */
@keyframes pc-index-about-bg-icon-float {
  0%,
  100% {
    transform: translate3d(0, 0, 0);
  }
  50% {
    transform: translate3d(0, -28px, 0);
  }
}

@keyframes pc-index-dj-bg-marquee {
  0% {
    transform: translate3d(0, 0, 0);
  }
  100% {
    /* 3×3 下一格 = 33.33%；相对 2×2 的 (50%,-50%) 周期，纹理呈左下→右上漂移 */
    transform: translate3d(33.333%, -33.333%, 0);
  }
}
</style>

<style lang="scss">
/* SimpleBar：自定义轨道/滑块（不依赖系统原生滚动条，避开 reset 与 overlay 滚动条问题） */
.pc-index__news-wrap-list {
  .simplebar-track.simplebar-vertical {
    width: 4px !important;
    background-color: #37fff1;
  }

  .simplebar-track.simplebar-horizontal {
    display: none;
  }

  .simplebar-scrollbar::before {
    left: 0 !important;
    right: 0 !important;
    top: 0 !important;
    bottom: 0 !important;
    border-radius: 0 !important;
    background-color: #fff380 !important;
    opacity: 1 !important;
  }

  .simplebar-scrollbar.simplebar-visible::before {
    opacity: 1 !important;
  }
}
</style>
